import { Injectable } from '@angular/core';

import { ParticipantMarketType, ParticipantOption, ParticipantPriceType } from '@cds/betting-offer';
import {
    FixtureParticipantPriceMap,
    HorseRaceSortingDirection,
    RacingParticipantWithEventOption,
    RacingParticipantWithParticipantOption,
    V3FixtureParticipantPriceMap,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';

import { HorsesFixtureParticipantHelper } from '../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { RacingService } from '../services/racing-service';

@Injectable()
export class ParticipantsService {
    constructor(
        private horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        private horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected racingService: RacingService,
    ) {}

    /**
     * Constructs runners with additional information for display.
     * @param participants Array of participants to be transformed.
     * @param options Map of participant options.
     * @param fixture Current game fixture.
     * @param spOptions Optional map of starting price options.
     * @returns Array of participants with extended attributes.
     */
    buildRunners(
        participants: WinGameParticipant[],
        options: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
        fixture: WinGameFixture,
        spOptions: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap = {},
    ): (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[] {
        return participants.map((p) => {
            const option = options[p.id];
            const spOption = spOptions[p.id];

            return {
                ...p,
                attributes: this.racingService.getTrapInfo(p),
                option,
                priceHistory: this.horsesFixturePricingHelper.getPriceHistory(option as ParticipantOption),
                isOptionActive: this.horsesFixturePricingHelper.isAvailableWinOption(option as ParticipantOption),
                availableBadges: [],
                easyBetViewStats: {},
                spOption,
                isSPOptionActive:
                    this.horsesFixturePricingHelper.isFixedOptionParticipant(p) &&
                    !this.horsesFixturePricingHelper.isPriceLocked(fixture, p, spOption as ParticipantOption) &&
                    this.racingService.isOptionVisible(p, ParticipantMarketType.Win, ParticipantPriceType.StartingPrice),
            } as RacingParticipantWithParticipantOption;
        });
    }

    /**
     * Orders participants based on the given sorting direction.
     * @param sortBy Direction to sort the participants.
     * @param participants Array of participants to be sorted.
     * @param fixture Current game fixture.
     * @param isSports Flag indicating if the sorting is for sports.
     * @returns Sorted array of participants.
     */
    orderParticipantsBy(
        sortBy: HorseRaceSortingDirection,
        participants: (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[],
        fixture: WinGameFixture,
        isSports: boolean,
    ): (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[] {
        if (!sortBy) return participants;

        const { horses, options, spOptions } = this.extractParticipantData(participants);

        const sortedRunners = isSports
            ? this.horsesFixtureSortingHelper.sortParticipantsByTrap(horses, options, sortBy)
            : this.sortParticipantsBy(horses, options, sortBy);

        const sortedParticipants = this.horsesFixtureParticipantHelper.getParticipants(sortedRunners);
        return this.buildRunners(sortedParticipants, options, fixture, spOptions);
    }

    /**
     * Sorts participants based on the specified sorting direction.
     * @param participants Array of participants to be sorted.
     * @param prices Map of participant prices.
     * @param sortBy Direction to sort the participants.
     * @returns Sorted array of participants.
     */
    sortParticipantsBy(
        participants: WinGameParticipant[],
        prices: FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
        sortBy: HorseRaceSortingDirection,
    ): WinGameParticipant[] {
        if (sortBy === HorseRaceSortingDirection.Odds) {
            return this.horsesFixtureSortingHelper.sortParticipantsByWinMarket(participants, prices);
        }
        return this.horsesFixtureSortingHelper.sortParticipantsByTrapNumber(participants);
    }

    /**
     * Extracts relevant data from participants for sorting and processing.
     * @param participants Array of participants.
     * @returns An object containing arrays and maps of extracted data.
     */
    private extractParticipantData(participants: (RacingParticipantWithEventOption | RacingParticipantWithParticipantOption)[]) {
        return participants.reduce(
            (aggr, cur) => {
                aggr.horses.push(cur);
                aggr.options[cur.id] = cur.option;
                aggr.spOptions[cur.id] = cur.spOption!;
                return aggr;
            },
            {
                horses: [] as WinGameParticipant[],
                options: {} as FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
                spOptions: {} as FixtureParticipantPriceMap | V3FixtureParticipantPriceMap,
            },
        );
    }
}
